import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Plus,
  Upload,
  Mail,
  Settings,
  Menu,
  X,
  Bell,
  User,
  LogOut,
  BarChart3,
  Shield,
  Briefcase,
  FileUp,
  PhoneOff,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
  Inbox as InboxIcon,
  CheckSquare,
  Webhook as WebhookIcon,
  GitMerge,
  Zap,
  BookOpen,
  Sparkles,
  Phone,
  Stethoscope,
  Bot,
  Megaphone
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import useClickOutside from '../hooks/useClickOutside';
import useNotificationStream from '../hooks/useNotificationStream';
import { OpenDentalBookingProvider } from '../contexts/OpenDentalBookingContext';
import PaymentOverdueBanner from './PaymentOverdueBanner';
import AssistantPanel from './assistant/AssistantPanel';
import { BrowserCallProvider } from '../contexts/BrowserCallContext';
import BrowserCallDock from './BrowserCallDock';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  const isSettingsPage = ['/smtp-settings', '/sms-settings', '/pipeline-settings', '/webhook-settings', '/custom-fields', '/automations', '/inbound-settings', '/team', '/knowledge-base', '/ai-auto-reply', '/claude-settings', '/phone-agent-settings'].includes(location.pathname);
  const isReportsPage = location.pathname === '/reports' || location.pathname.startsWith('/reports/');
  // Generic open-state for collapsible groups (Settings, Reports, future ones).
  // Keyed by group name so we don't need a new state hook per group.
  const [openGroups, setOpenGroups] = useState({
    Settings: isSettingsPage,
    Reports: isReportsPage,
  });
  const toggleGroup = (name) => setOpenGroups((g) => ({ ...g, [name]: !g[name] }));
  const navigate = useNavigate();
  const { user, organization, logout } = useAuth();
  const queryClient = useQueryClient();

  // Single app-wide SSE listener: real-time bell + opt-in on-screen popups
  // (enabled per-rule in Automations via notify_user's showPopup flag).
  useNotificationStream(user);

  // Auto-open the matching group when navigating into one of its children
  useEffect(() => {
    if (isSettingsPage) setOpenGroups((g) => ({ ...g, Settings: true }));
    if (isReportsPage) setOpenGroups((g) => ({ ...g, Reports: true }));
  }, [isSettingsPage, isReportsPage]);

  // Bildirimleri getir
  const { data: notificationsData } = useQuery(
    'notifications',
    () => notificationsAPI.getNotifications(),
    {
      refetchInterval: 10000, // 10 saniyede bir güncelle
      onError: (error) => {
        console.error('Error fetching notifications:', error);
      }
    }
  );

  // Okunmamış bildirim sayısını getir
  const { data: unreadData } = useQuery(
    'unread-notifications',
    () => notificationsAPI.getUnreadCount(),
    {
      refetchInterval: 10000,
      onError: (error) => {
        console.error('Error fetching unread notification count:', error);
      }
    }
  );

  // Bildirimi okundu olarak işaretle
  const markAsReadMutation = useMutation(
    notificationsAPI.markAsRead,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notifications');
      },
      onError: (error) => {
        console.error('Error marking notification as read:', error);
        toast.error('An error occurred while marking notification as read');
      }
    }
  );

  // Tüm bildirimleri okundu olarak işaretle (zile tıklayınca tek seferde)
  const markAllAsReadMutation = useMutation(
    notificationsAPI.markAllAsRead,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notifications');
      },
      onError: (error) => {
        console.error('Error marking all notifications as read:', error);
      }
    }
  );

  const notifications = notificationsData?.data?.notifications || [];
  const unreadNotifications = unreadData?.data?.count || 0;

  // Facebook-style tab badge: "(3) CRM - Gain Rhino" while there are unread
  // notifications, so background tabs signal new leads at a glance.
  useEffect(() => {
    const base = 'CRM - Gain Rhino';
    document.title = unreadNotifications > 0 ? `(${unreadNotifications}) ${base}` : base;
  }, [unreadNotifications]);
  const unreadInboxCount = notifications.filter(
    (n) => !n.read && n.type === 'inbound_message'
  ).length;

  const openNotifications = () => {
    const wasClosed = !showNotifications;
    setShowNotifications(!showNotifications);
    if (wasClosed && unreadNotifications > 0) {
      // Optimistically clear the badge so the UI snaps immediately
      queryClient.setQueryData('unread-notifications', (old) => {
        if (!old) return old;
        return { ...old, data: { ...(old.data || {}), count: 0 } };
      });
      queryClient.setQueryData('notifications', (old) => {
        if (!old?.data?.notifications) return old;
        return {
          ...old,
          data: {
            ...old.data,
            notifications: old.data.notifications.map((n) => ({ ...n, read: true }))
          }
        };
      });
      markAllAsReadMutation.mutate();
    }
  };

  const markAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  // Bildirime tıklama: artık "okundu" zaten zil açıldığında işlendi —
  // tek görevimiz ilgili sayfaya yönlendirmek.
  const handleNotificationClick = (notification) => {
    if (!notification.read) markAsRead(notification._id);

    if (notification.type === 'inbound_message') {
      if (notification.data?.live_call && notification.data?.leadId) {
        // "AI is answering a call" — the live panel lives on the lead,
        // not in the Inbox (same target the popup click uses).
        navigate(`/leads/${notification.data.leadId}`);
      } else {
        // Inbox opens and focuses the matching conversation
        navigate('/inbox');
      }
    } else if (notification.type === 'bulk_email' && notification.data?.campaignId) {
      navigate('/bulk-email');
    } else if (notification.data?.leadId) {
      navigate(`/leads/${notification.data.leadId}`);
    }

    setShowNotifications(false);
  };

  const notificationsRef = useClickOutside(showNotifications, () => setShowNotifications(false));

  // Build navigation based on user permissions
  const buildNavigation = () => {
    const nav = [];
    const permissions = user?.permissions || {};

    // Dashboard
    if (permissions.view_dashboard !== false) {
      nav.push({ name: 'Dashboard', href: '/', icon: Home });
    }

    // Inbox — badge with unread inbound count
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Inbox', href: '/inbox', icon: InboxIcon, badge: unreadInboxCount });
    }

    // Tasks
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Tasks', href: '/tasks', icon: CheckSquare });
    }

    // Leads
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Leads', href: '/leads', icon: Users });
    }

    // Pipeline (Kanban)
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Pipeline', href: '/pipeline', icon: LayoutGrid });
    }

    // Do Not Call
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Do Not Call', href: '/do-not-call', icon: PhoneOff });
    }

    // Duplicates
    if (permissions.edit_lead !== false) {
      nav.push({ name: 'Duplicates', href: '/duplicates', icon: GitMerge });
    }

    // Customers
    if (permissions.view_leads !== false) {
      nav.push({ name: 'Customers', href: '/customers', icon: Briefcase });
    }

    // New Lead
    if (permissions.create_lead !== false) {
      nav.push({ name: 'New Lead', href: '/leads/new', icon: Plus });
    }

    // CSV Import
    if (permissions.create_lead !== false) {
      nav.push({ name: 'CSV Import', href: '/import-csv', icon: FileUp });
    }

    // Upload Business Card
    if (permissions.upload_business_card !== false) {
      nav.push({ name: 'Upload Business Card', href: '/upload', icon: Upload });
    }

    // Bulk Email
    if (permissions.send_bulk_email !== false) {
      nav.push({ name: 'Bulk Email', href: '/bulk-email', icon: Mail });
    }

    // Reports (with children)
    if (permissions.view_reports !== false) {
      nav.push({
        name: 'Reports',
        icon: BarChart3,
        children: [
          { name: 'Overview',         href: '/reports',       icon: BarChart3 },
          { name: 'User Performance', href: '/reports/users', icon: Users },
          { name: 'Ad Attribution',   href: '/reports/ads',   icon: Megaphone },
        ]
      });
    }



    // Settings (with children). Visible if the user has *any* settings-
    // category permission so Team-only admins (manage_users) still see
    // the group even when SMTP/automations are locked off.
    const canSeeSmtpSettings = permissions.manage_smtp_settings !== false;
    const canManageUsers = permissions.manage_users !== false;
    {
      const settingsChildren = [];
      if (canManageUsers) {
        settingsChildren.push({ name: 'Team Members', href: '/team', icon: Users });
      }
      if (canSeeSmtpSettings) {
        settingsChildren.push(
          { name: 'SMTP Settings', href: '/smtp-settings', icon: Mail },
        );
        // Add-on gated entries — only show when the SuperAdmin has
        // enabled the corresponding feature flag for this org. The
        // matching API route also enforces this server-side, so
        // hiding the menu here is purely UX.
        // SMS rides on the VoIP add-on (VoIP.ms's sendSMS/getSMS), so
        // its menu entry is gated on the same flag — hiding it avoids
        // a dead-end where the user clicks SMS Settings only to find a
        // page that points them to a VoIP Settings link they don't
        // have access to either.
        if (organization?.voip?.enabled) {
          settingsChildren.push(
            { name: 'SMS Settings', href: '/sms-settings', icon: MessageSquare },
            { name: 'VoIP Settings', href: '/voip-settings', icon: Phone },
          );
        }
        if (organization?.opendental?.enabled) {
          settingsChildren.push({ name: 'OpenDental', href: '/opendental-settings', icon: Stethoscope });
        }
        if (organization?.phoneAgent?.enabled) {
          settingsChildren.push({ name: 'Phone Agent', href: '/phone-agent-settings', icon: Bot });
        }
        settingsChildren.push(
          { name: 'Inbound Channels', href: '/inbound-settings', icon: InboxIcon },
          { name: 'Pipeline Settings', href: '/pipeline-settings', icon: LayoutGrid },
          { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen },
          { name: 'AI Auto-Reply', href: '/ai-auto-reply', icon: Sparkles },
          { name: 'Custom Fields', href: '/custom-fields', icon: Settings },
          { name: 'Automations', href: '/automations', icon: Zap },
          { name: 'Webhooks', href: '/webhook-settings', icon: WebhookIcon },
        );
      }
      // Visible to every user — the MCP connection key is personal and
      // scoped to the user's own permissions, so reps get this too (it's
      // the only Settings entry they see).
      settingsChildren.push({ name: 'Connect Claude', href: '/claude-settings', icon: Bot });
      nav.push({ name: 'Settings', icon: Settings, children: settingsChildren });
    }

    // Super Admin (only for superadmin)
    if (user?.role === 'superadmin') {
      nav.push({ name: 'Super Admin', href: '/superadmin', icon: Shield });
    }

    return nav;
  };

  const navigation = buildNavigation();

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Child-link variant: exact path or strict sub-path. Avoids /reports
  // matching /reports/users (and vice-versa) when both are siblings.
  const isChildActive = (href) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <OpenDentalBookingProvider>
    <BrowserCallProvider>
    <div className="min-h-screen bg-gray-50 m-0 p-0 overflow-x-hidden">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div className="hidden lg:flex flex-col fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg">
        <div className="flex-shrink-0 flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 flex items-center justify-center">
              <img
                src="/logo.png"
                alt="CRM Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Logo yüklenemezse fallback olarak text göster
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white font-bold text-sm">CRM</span>
              </div>
            </div>
            <h1 className="ml-3 text-xl font-semibold text-gray-900">CRM System</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto mt-6 px-3 pb-4">
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;

              // Children varsa expandable menü
              if (item.children) {
                const hasActiveChild = item.children.some(child => isChildActive(child.href));
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleGroup(item.name)}
                      className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${hasActiveChild
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                    >
                      <div className="flex items-center">
                        <Icon
                          size={20}
                          className={`mr-3 ${hasActiveChild ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                        />
                        {item.name}
                      </div>
                      {openGroups[item.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <AnimatePresence>
                      {openGroups[item.name] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-4 mt-1 space-y-1">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isChildActive(child.href)
                                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                  <ChildIcon
                                    size={18}
                                    className={`mr-3 ${isChildActive(child.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                                  />
                                  {child.name}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const hasBadge = typeof item.badge === 'number' && item.badge > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors duration-200 ${isActive(item.href)
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600 font-medium'
                    : hasBadge
                      ? 'text-gray-900 hover:bg-gray-50 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
                    }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center">
                    <Icon
                      size={20}
                      className={`mr-3 ${isActive(item.href) ? 'text-primary-600' : hasBadge ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                    />
                    {item.name}
                  </div>
                  {hasBadge && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <User size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</p>
            </div>
          </div>
        </div>

      </div>

      {/* Mobile Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: sidebarOpen ? 0 : -300 }}
        transition={{ type: 'spring', damping: 20 }}
        className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg flex flex-col"
      >
        <div className="flex-shrink-0 flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 flex items-center justify-center">
              <img
                src="/logo.png"
                alt="CRM Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Logo yüklenemezse fallback olarak text göster
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white font-bold text-sm">CRM</span>
              </div>
            </div>
            <h1 className="ml-3 text-xl font-semibold text-gray-900">CRM System</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto mt-6 px-3 pb-4">
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;

              // Children varsa expandable menü (mobile)
              if (item.children) {
                const hasActiveChild = item.children.some(child => isChildActive(child.href));
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleGroup(item.name)}
                      className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${hasActiveChild
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                    >
                      <div className="flex items-center">
                        <Icon
                          size={20}
                          className={`mr-3 ${hasActiveChild ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                        />
                        {item.name}
                      </div>
                      {openGroups[item.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <AnimatePresence>
                      {openGroups[item.name] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-4 mt-1 space-y-1">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isChildActive(child.href)
                                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  <ChildIcon
                                    size={18}
                                    className={`mr-3 ${isChildActive(child.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                                  />
                                  {child.name}
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const hasBadge = typeof item.badge === 'number' && item.badge > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors duration-200 ${isActive(item.href)
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600 font-medium'
                    : hasBadge
                      ? 'text-gray-900 hover:bg-gray-50 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
                    }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center">
                    <Icon
                      size={20}
                      className={`mr-3 ${isActive(item.href) ? 'text-primary-600' : hasBadge ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                    />
                    {item.name}
                  </div>
                  {hasBadge && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <User size={16} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <div className="fixed top-0 right-0 left-0 lg:left-64 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8 relative">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <Menu size={20} />
              </button>
              <h2 className="ml-2 lg:ml-0 text-lg font-medium text-gray-900">
                {(() => {
                  for (const item of navigation) {
                    if (item.href && isActive(item.href)) return item.name;
                    if (item.children) {
                      const child = item.children.find(c => isChildActive(c.href));
                      if (child) return child.name;
                    }
                  }
                  return 'Page';
                })()}
              </h2>
            </div>

            <div className="flex items-center space-x-4 relative" ref={notificationsRef}>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md relative"
                onClick={openNotifications}
                title="Notifications"
              >
                <Bell size={20} />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
              <button
                onClick={logout}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                title="Logout"
              >
                <LogOut size={20} />
              </button>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-12 right-0 z-50 w-80 bg-white rounded-lg shadow-lg border border-gray-200"
                  >
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification._id}
                            className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${!notification.read ? 'bg-blue-50' : ''
                              }`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className={`w-2 h-2 rounded-full ${notification.type === 'lead_created' ? 'bg-green-500' :
                                  notification.type === 'email_sent' ? 'bg-blue-500' :
                                    notification.type === 'bulk_email' ? 'bg-purple-500' :
                                      'bg-gray-500'
                                  }`}></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  {new Date(notification.createdAt).toLocaleString('en-US', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center">
                          <Bell size={48} className="mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="pt-16 px-2 sm:px-4 lg:px-6">
          <PaymentOverdueBanner />
          {children}
        </main>
      </div>
      <AssistantPanel />
      <BrowserCallDock />
    </div>
    </BrowserCallProvider>
    </OpenDentalBookingProvider>
  );
};

export default Layout; 